import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import {
  BinocularsIcon,
  BookmarkIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockClockwiseIcon,
  ClockCounterClockwiseIcon,
  FadersIcon,
  TrendUpIcon,
} from '@phosphor-icons/react';
import { TooltipProps } from 'recharts';
import { ContentType } from 'recharts/types/component/Tooltip';
import { Heatmap, LineChart } from '@mantine/charts';
import {
  ActionIcon,
  BoxProps,
  CheckIcon,
  Combobox,
  Divider,
  Group,
  LoadingOverlay,
  Menu,
  Paper,
  Pill,
  PillsInput,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  useCombobox,
} from '@mantine/core';
import { DatePickerInput, DatePickerProps } from '@mantine/dates';
import { useElementSize } from '@mantine/hooks';
import { Species } from './models';
import { useStore } from './store';

const SelectOption = ({ label, latin_name }: Species) => {
  return (
    <Group wrap="nowrap">
      <div>
        <Text size="sm">{label}</Text>
        <Text size="xs" opacity={0.65}>
          {latin_name}
        </Text>
      </div>
    </Group>
  );
};

const SpeciesMultiSelectBox = ({ species, ...props }: { species: Species[] } & BoxProps) => {
  const [search, setSearch] = useState<string>('');
  const id_species = useStore((state) => state.id_species);
  const setIdSpecies = useStore((state) => state.setIdSpecies);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  });

  const handleValueSelect = (value: string) => {
    const id = parseInt(value, 10);
    setIdSpecies(
      id_species.includes(id) ? id_species.filter((v) => v !== id) : [...id_species, id]
    );
    setSearch('');
    combobox.closeDropdown();
  };

  const handleValueRemove = (id: number) => setIdSpecies(id_species.filter((v) => v !== id));

  const renderOptions = () => {
    const results = species
      .filter(
        (item) => !search || item.search_label.toUpperCase().indexOf(search.toUpperCase()) > -1
      )
      .filter((_, index) => index < 50);
    return results.length > 0 ? (
      results.map((item) => (
        <Combobox.Option
          value={item.id_species.toString()}
          key={item.id}
          active={id_species.includes(item.id_species)}
        >
          <Group gap="sm">
            {id_species.includes(item.id_species) ? <CheckIcon size={12} /> : null}
            <SelectOption {...item} />
          </Group>
        </Combobox.Option>
      ))
    ) : (
      <Combobox.Empty>Pas de résultat...</Combobox.Empty>
    );
  };

  return (
    <Combobox store={combobox} onOptionSubmit={handleValueSelect} withinPortal={false}>
      <Combobox.DropdownTarget>
        <PillsInput
          {...props}
          description="Espèces"
          onClick={() => combobox.openDropdown()}
          styles={{
            input: {
              minHeight: '75px',
            },
          }}
        >
          <Pill.Group>
            {id_species.map((id) => (
              <Pill size="md" key={id} withRemoveButton onRemove={() => handleValueRemove(id)}>
                {species.find((s) => s.id_species === id)?.label}
              </Pill>
            ))}
            <Combobox.EventsTarget>
              <PillsInput.Field
                onFocus={() => combobox.openDropdown()}
                onBlur={() => combobox.closeDropdown()}
                value={search}
                placeholder="Recherche libre"
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  combobox.updateSelectedOptionIndex();
                  setSearch(event.currentTarget.value);
                  if (event.currentTarget.value && !combobox.dropdownOpened) {
                    combobox.openDropdown();
                  }
                }}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Backspace' && search.length === 0) {
                    event.preventDefault();
                    handleValueRemove(id_species[id_species.length - 1]);
                  }
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea.Autosize type="scroll" mah={200}>
            {renderOptions()}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export const AppFilter = () => {
  const range = useStore((state) => state.range);
  const count = useStore((state) => state.count);

  const calcDayDiff = useStore((state) => state.calcDayDiff);
  const stats = useStore((state) => state.stats);
  const date = useStore((state) => state.date);
  const series = useStore((state) => state.series);
  const id_species = useStore((state) => state.id_species);
  const threshold = useStore((state) => state.threshold);
  const heatmapData = useStore((state) => state.heatmapData);
  const next = useStore((state) => state.next);
  const setIdSpecies = useStore((state) => state.setIdSpecies);
  const setThreshold = useStore((state) => state.setThreshold);
  const setRange = useStore((state) => state.setRange);
  const setDate = useStore((state) => state.setDate);
  const today = dayjs();

  const { ref: heatmapGroupRef, width } = useElementSize();

  const dayRenderer: DatePickerProps['renderDay'] = (date: string) => {
    return (
      <div
        style={{
          textDecoration: today.isSame(dayjs(date), 'day') ? 'underline' : 'inherit',
        }}
      >
        {new Date(date).getDate()}
      </div>
    );
  };

  useEffect(() => {
    calcDayDiff();
  }, [range]);

  // @ts-ignore
  const barchartTooltip: ContentType<any, any> = ({ payload }: TooltipProps<any, any>) => (
    <Paper px="md" py="sm" withBorder shadow="md" radius="md">
      {payload && payload.length > 0 && (
        <Text fw={500} mb={5}>
          {payload[0].payload.label}
        </Text>
      )}
      {payload && payload.length > 0 && (
        <Text c={payload[0].color} fz="sm">
          Observations: {payload[0].payload.count}
        </Text>
      )}
    </Paper>
  );

  const heatmapGap = 1;
  const heatmapRatio = 6.85; // chiffre empirique validé avec le max width à 300px

  return date && stats && stats.species && stats.species.length > 0 ? (
    <div>
      <Divider
        mb={4}
        labelPosition="left"
        label={
          <>
            <FadersIcon size={16} />
            <Text ml={5} size="sm" fw={700}>
              Filtres
            </Text>
          </>
        }
      />
      <Group gap={4} mt={4} wrap="nowrap" align="start">
        <Stack flex={1}>
          <SpeciesMultiSelectBox flex={1} species={stats.species || []} />
          <DatePickerInput
            mt={8}
            description="Période"
            /*styles={() => ({
            month: {
              width: "100%",
            },
            calendarHeader: {
              width: "100%",
              maxWidth: "100%",
            },
          })}*/
            presets={[
              {
                value: [
                  dayjs(stats.last as Date).format('YYYY-MM-DD'),
                  dayjs(stats.last as Date).format('YYYY-MM-DD'),
                ],
                label: 'Jour',
              },
              {
                value: [
                  dayjs(stats.last as Date)
                    .subtract(1, 'week')
                    .format('YYYY-MM-DD'),
                  dayjs(stats.last as Date).format('YYYY-MM-DD'),
                ],
                label: 'Semaine',
              },
              {
                value: [
                  dayjs(stats.last as Date)
                    .subtract(1, 'month')
                    .format('YYYY-MM-DD'),
                  dayjs(stats.last as Date).format('YYYY-MM-DD'),
                ],
                label: 'Mois',
              },
              {
                value: [
                  dayjs(stats.last as Date)
                    .subtract(1, 'year')
                    .format('YYYY-MM-DD'),
                  dayjs(stats.last as Date).format('YYYY-MM-DD'),
                ],
                label: 'Année',
              },
              {
                value: [
                  dayjs(stats.first as Date).format('YYYY-MM-DD'),
                  dayjs(stats.last as Date).format('YYYY-MM-DD'),
                ],
                label: 'Tout',
              },
            ]}
            valueFormat="DD/MM/YYYY"
            __clearable={false}
            labelSeparator=" → "
            type="range"
            size="sm"
            value={range}
            allowSingleDateInRange
            onChange={setRange}
            date={date}
            onDateChange={setDate}
            minDate={stats.first as Date}
            maxDate={stats.last as Date}
            clearable
            renderDay={dayRenderer}
          />
        </Stack>
        <Stack flex={0}>
          <Menu position="bottom-start">
            <Menu.Target>
              <Tooltip label="Favoris">
                <ActionIcon size="lg" mt="lg" variant="light">
                  <BookmarkIcon size={18} />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item onClick={() => setIdSpecies([329])}>Martin-pêcheur</Menu.Item>
              <Menu.Item onClick={() => setIdSpecies([332, 347, 374])}>Huppées</Menu.Item>
              <Menu.Item onClick={() => setIdSpecies([330])}>Guêpier d'Europe</Menu.Item>
              <Menu.Item onClick={() => setIdSpecies([488, 489, 486, 1065, 490])}>
                Pie-grièches
              </Menu.Item>
              <Menu.Item onClick={() => setIdSpecies([320, 321, 317, 322, 315, 316, 323])}>
                Hibous/Chouettes
              </Menu.Item>
              <Menu.Item onClick={() => setIdSpecies([44, 588])}>Ibis falcinelles/sacrés</Menu.Item>
            </Menu.Dropdown>
          </Menu>
          <Tooltip label="Période précédente">
            <ActionIcon
              size="lg"
              mt={4}
              disabled={!(range && range[0] && range[1] && new Date(range[0]) > stats.first)}
              onClick={() => next(-1)}
              variant="light"
            >
              <ClockCounterClockwiseIcon size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Période suivante">
            <ActionIcon
              size="lg"
              mt={4}
              disabled={!(range && range[0] && range[1] && new Date(range[1]) < stats.last)}
              onClick={() => next(1)}
              variant="light"
            >
              <ClockClockwiseIcon size={18} />
            </ActionIcon>
          </Tooltip>
          {(id_species || []).length === 0 && (
            <Menu width={200} position="bottom-start">
              <Menu.Target>
                <Tooltip label="Observations peu courantes">
                  <ActionIcon size="lg" variant="light" mt={4}>
                    <BinocularsIcon size={18} />
                  </ActionIcon>
                </Tooltip>
              </Menu.Target>

              <Menu.Dropdown>
                {[0, 10, 50, 100, 200, 500, 1000, 2000, 5000, 1000].map((c) => (
                  <Menu.Item
                    leftSection={threshold === c ? <CheckCircleIcon size={14} /> : <></>}
                    key={c}
                    onClick={() => setThreshold(c)}
                  >
                    {c ? <span>&lt; {c} observations</span> : <span>Toutes les observations</span>}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}
        </Stack>
      </Group>
      <Divider
        mt={16}
        mb={4}
        labelPosition="left"
        label={
          <>
            <TrendUpIcon size={16} />
            <Tooltip label={`Total : ${stats.total}`}>
              <Text ml={5} size="sm" fw={700}>
                Tendance résultats ({count})
              </Text>
            </Tooltip>
          </>
        }
      />
      <LineChart
        h={120}
        px={8}
        data={series}
        gridAxis="y"
        curveType="natural"
        gridProps={{ yAxisId: 'left' }}
        yAxisProps={{
          orientation: 'right',
          width: 30,
        }}
        withYAxis={false}
        tickLine="none"
        tooltipProps={{
          content: barchartTooltip,
        }}
        dataKey="period"
        series={[{ name: 'count', label: 'Observations', color: 'blue.6' }]}
      />
      <Divider
        mt={16}
        mb={4}
        labelPosition="left"
        label={
          <>
            <ChartBarIcon size={16} />
            <Text ml={5} size="sm" fw={700}>
              Distribution annuelle
            </Text>
          </>
        }
      />
      <Stack ref={heatmapGroupRef} justify="stretch">
        {heatmapData &&
          width &&
          Object.keys(heatmapData.data)
            .sort()
            .reverse()
            .map((year: string) => (
              <Stack key={year}>
                <Text size="xs" ta="center" mb={0}>
                  {year}
                </Text>
                <Heatmap
                  mb="xs"
                  data={heatmapData.data[year]}
                  domain={[1, heatmapData.maxCount]}
                  monthLabels={[
                    '01',
                    '02',
                    '03',
                    '04',
                    '05',
                    '06',
                    '07',
                    '08',
                    '09',
                    '10',
                    '11',
                    '12',
                  ]}
                  startDate={`${year}-01-01`}
                  endDate={`${year}-12-31`}
                  rectSize={Math.min(
                    width / (Object.keys(heatmapData.data[year]).length / heatmapRatio) -
                      heatmapGap -
                      0,
                    10
                  )}
                  rectRadius={1}
                  gap={heatmapGap}
                  withTooltip
                  withMonthLabels
                  //splitMonths={true}
                  getTooltipLabel={({ date, value }) =>
                    `${dayjs(date).format('YYYY-MM-DD')} - ${value === null || value === 0 ? '0 observations' : `${value} observation${value > 1 ? 's' : ''}`}`
                  }
                />
              </Stack>
            ))}
      </Stack>
    </div>
  ) : (
    <LoadingOverlay visible />
  );
};
